// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type ThunkDispatch } from '@reduxjs/toolkit';
import {
  type InternalTargetPortfolioResponseBody,
  type OperationResult,
  type TargetPortfolioPosition,
  type InternalTargetPortfolioPosition,
  type TargetPortfolioResponse,
} from '../../interfaces/api/brokerage/proposals-types';
import { getAssetsInfo } from '../assets/get-assets-info';
import { type AssetInfo } from '../../interfaces/api/ai/assets';
import { type AdvisoryInstitution } from '../../types/brokerage-types';
import { TARGET_PORTFOLIO_CASH_TICKER } from './map-target-portfolio-request';

/**
 * Target Portfolio Position with Asset Info
 *
 * This type extends the internal target portfolio response body by adding
 * asset_class and asset_type properties.
 *
 * @property asset_class - The asset class of the position.
 * @property asset_type - The asset type of the position.
 */
export type TargetPortfolioPositionWithAssetInfo =
  InternalTargetPortfolioResponseBody & {
    asset_class: string;
    asset_type: string;
    currency: string | null;
    institution_name: string | null;
    price: number;
    details: AssetInfo | null;
  };

/** Enriched target-portfolio simulation, including the advisory envelope fields. */
export type CreateTargetPortfolioResult = {
  positions: TargetPortfolioPositionWithAssetInfo[];
  status: TargetPortfolioResponse['status'];
  operation_results: OperationResult[];
};

/** True when the simulation fully succeeded and every operation was applied. */
export const isTargetPortfolioSimulationSuccessful = (
  result:
    | Pick<CreateTargetPortfolioResult, 'status' | 'operation_results'>
    | undefined,
): boolean =>
  result?.status === 'success' &&
  (result.operation_results ?? []).every(
    operation => operation.status === 'applied',
  );

/**
 * Build Trader Target Portfolio
 *
 * This function builds a target portfolio by fetching asset information and adding
 * asset_class and asset_type properties to the positions. It also maps
 * institution_id from iban using the current_portfolio positions.
 *
 * @param data - The target portfolio positions to build.
 * @param currentPortfolio - The current portfolio positions with iban and
 *   institution_id mapping.
 * @param institutions - List of client institutions.
 * @param dispatch - The dispatch function to dispatch actions.
 * @returns A list of {@link TargetPortfolioPositionWithAssetInfo} entries.
 */
export const buildTraderTargetPortfolio = async (
  data: TargetPortfolioPosition[],
  currentPortfolio: InternalTargetPortfolioPosition[],
  institutions: AdvisoryInstitution[],
  dispatch: ThunkDispatch<any, any, any>,
): Promise<TargetPortfolioPositionWithAssetInfo[]> => {
  const institutionsMap = new Map(
    institutions.map(institution => [institution.id, institution]),
  );

  const ibans = new Map(
    currentPortfolio.map(position => [position.iban, position.institution_id]),
  );

  const isCashPosition = (position: TargetPortfolioPosition): boolean =>
    position.ticker === TARGET_PORTFOLIO_CASH_TICKER ||
    position.isin === TARGET_PORTFOLIO_CASH_TICKER;

  const tickers = data
    .filter(position => !isCashPosition(position))
    .map(({ ticker, isin }) => ticker ?? isin);

  const payload = Array.from(new Set(tickers.filter(Boolean))) as string[];

  const assetsInfoResponse =
    payload.length > 0 ? await getAssetsInfo(payload, dispatch) : [];

  const assetsInfo: Map<string, AssetInfo> = new Map(
    assetsInfoResponse.flatMap(asset => {
      const entries: [string, AssetInfo][] = [];
      if (asset.ticker) {
        entries.push([asset.ticker, asset]);
      }
      if (asset.isin) {
        entries.push([asset.isin, asset]);
      }
      return entries;
    }),
  );

  const getClosestValue = (
    obj: Map<string, AssetInfo>,
    key: string,
  ): AssetInfo | undefined => {
    const matchingKey = Array.from(obj.keys()).find(k => k.includes(key));
    return matchingKey ? obj.get(matchingKey) : undefined;
  };

  const assetTypeFormatter = (assetType: string) => {
    const types = {
      'mutual funds': 'fund',
      stocks: 'stock',
      mutual_fund: 'fund',
      equity: 'stock',
      funds: 'fund',
    };

    return assetType
      ? types[assetType as keyof typeof types] || assetType
      : 'other';
  };

  return data.map(position => {
    const institutionId = ibans.get(position.iban);
    const institution = institutionId
      ? institutionsMap.get(institutionId)
      : undefined;
    const account = institution?.products.accounts.find(
      ({ iban }) => iban === position.iban,
    );

    if (isCashPosition(position)) {
      return {
        ...position,
        institution_id: institutionId!,
        institution_name: institution?.name ?? null,
        currency: account?.currency ?? null,
        asset_class: 'Cash',
        asset_type: 'currency',
        price:
          position.quantity > 0
            ? position.amount / position.quantity
            : position.amount,
        details: null,
      };
    }

    const assetInfo = getClosestValue(
      assetsInfo,
      position.ticker ?? position.isin!,
    );

    const price = position.quantity > 0 ? position.amount / position.quantity : 0;

    return {
      ...position,
      institution_id: institutionId!,
      institution_name: institution?.name ?? null,
      currency: assetInfo?.currency ?? null,
      asset_class: assetInfo ? assetInfo?.asset_class || 'Equity' : 'Other',
      asset_type: assetTypeFormatter(assetInfo?.asset_type ?? ''),
      price,
      details: assetInfo || null,
    };
  });
};
